import type {DumpOptions} from 'js-yaml'
import {dump, load} from 'js-yaml'

import type {SerializedError} from '@reduxjs/toolkit'
import type {FetchBaseQueryError} from '@reduxjs/toolkit/query'
import omit from 'lodash/omit'

import {extractErrorMessage} from '../../../../utils/extractError'
import {
  customAgentsNames,
  jobCountHardLimit,
  jobCountSoftLimit,
} from '../../../../utils/featureToggles'
import type {NewIntegrationDto, NewVcsRootDto} from '../services/pipelinesApi.types'
import type {Dependency, PipelineSettings, RunsOn, SelfHostedRunsOn} from '../types'

export const parsePipeline = (yaml: string): PipelineSettings => {
  const parsedPipeline = load(yaml) as PipelineSettings

  if (
    parsedPipeline['import-parameters'] != null &&
    !Array.isArray(parsedPipeline['import-parameters'])
  ) {
    parsedPipeline['import-parameters'] = []
  }

  if (!parsedPipeline.jobs) {
    return parsedPipeline
  }

  // TW-95254 Custom deserialization for integrations
  for (const [_, job] of Object.entries(parsedPipeline.jobs)) {
    if (job.integrations && job.integrations.length > 0) {
      job.integrations = job.integrations
        .map(integration => Object.entries(integration)[0])
        .filter(integration => integration.length === 2)
        .map(integration => ({
          id: integration[1],
          type: integration[0],
        }))
    }
  }

  return parsedPipeline
}
export const stringifyPipeline: (pipeline: PipelineSettings, opts?: DumpOptions) => string = dump

export const getDependencyName = (dependency: Dependency) =>
  typeof dependency === 'string' ? dependency : Object.keys(dependency)[0]

export const getPipelinesSettingsWithoutDeprecatedFields = (settings: PipelineSettings) =>
  omit(settings, ['name'])

export const getPreformattingSettings = (key: string, value: any) => {
  const isJobsSection = key === 'jobs'
  const isValueString = typeof value === 'string'
  const isIntegrationsSection = key === 'integrations'
  const isImportedParametersSection = key === 'importParameters'

  if (isValueString) {
    return value.trim()
  }

  if (isJobsSection && typeof value === 'object') {
    return Object.keys(value).reduce((accumulator, jobName) => {
      const jobValue = value[jobName]
      const jobNameFormatted = jobName.trim()

      return {
        ...accumulator,
        [jobNameFormatted]: jobValue,
      }
    }, {})
  }

  if (isIntegrationsSection && Array.isArray(value) && value.length > 0) {
    return value
      .filter(integration => integration.type && integration.id)
      .map(integration => ({
        [`${integration.type}`]: integration.id,
      }))
  }

  if (isImportedParametersSection) {
    return value
  }

  return value
}

export const getPreformattingIntegrations = (draft: NewIntegrationDto[]) => {
  draft?.forEach(({parameters}) => {
    for (const [key, value] of Object.entries(parameters)) {
      if (value == null) {
        delete parameters[key]
      }
    }
  })
}

export const getPreformattingVcsRoot = (draft?: NewVcsRootDto) => {
  if (!draft) {
    return
  }

  if (draft.isExternal) {
    draft.externalVcsRootId = draft.id
  }

  draft.statusPublishingEnabled = draft.isStatusPublishingEnabled
  draft.pullRequestsTrackingEnabled = draft.trackPullRequests
}

export const getPreformattingVcsRoots = (draft?: NewVcsRootDto[]) => {
  draft?.forEach(getPreformattingVcsRoot)
}

export const getCustomAgents = (): Array<{name: string}> => {
  const customAgentsProp = customAgentsNames || null
  return customAgentsProp
    ? customAgentsProp
        .split(',')
        .filter(Boolean)
        .map((i: string) => ({name: i.trim()}))
    : []
}

export const jobLimits = {
  soft: jobCountSoftLimit,
  hard: jobCountHardLimit,
}

export const getExtractErrorMessages = (
  error: SerializedError | FetchBaseQueryError | string | null | undefined,
) => {
  try {
    if (error != null) {
      const extractedError = extractErrorMessage(error)

      if (typeof extractedError === 'string') {
        const errors = JSON.parse(extractedError).errors

        if (Array.isArray(errors)) {
          return errors
        }
      }
    }
  } catch (e) {
    return null
  }

  return null
}

export function isSelfHostedRunsOn(runsOn: RunsOn | undefined): runsOn is SelfHostedRunsOn {
  return typeof runsOn === 'object' ? 'self-hosted' in runsOn : runsOn === 'self-hosted'
}
